// core
import React from "react";

// components
import LModal from "../../../../common/components/Modals/LModal/LModal";
import ModalHeader from "../../../../common/components/Modals/ModalHeader/ModalHeader";

interface ISecurityAndPrivacyModal {
    isOpen: boolean;
    onClose: () => void;
}

const SecurityAndPrivacyModal: React.FC<ISecurityAndPrivacyModal> = (props) => {
    return (
        <React.Fragment>
            <LModal
                Header={() => (
                    <ModalHeader
                        onClose={props.onClose}
                        title="Website Security &amp; Privacy"
                    />
                )}
                calcHeightMode="auto"
                width="wide"
                isOpen={props.isOpen}
                onClose={props.onClose}>
                {() => (
                    <React.Fragment>
                        <p>
                            This policy is about how we collect, use, disclose and protect
                            your personal information. We take our responsibility to
                            protect your personal information very seriously. We collect
                            and use your personal information to deliver our services.
                            We&apos;re committed to following four principles in relation
                            to your information:
                        </p>
                        <ul>
                            <li>
                                We will respect your privacy—our brand is built on trust
                                and integrity.
                            </li>
                            <li>
                                We&apos;re committed to being transparent and honest, so
                                you know what we do with your personal information
                                (including if something goes wrong).
                            </li>
                            <li>
                                We use personal information to add value to your life.
                            </li>
                            <li>
                                We avoid getting involved in anything that could breach
                                your trust.
                            </li>
                        </ul>
                        <p>
                            If you have any questions about your privacy or this policy,
                            please <a href="/our-service/contact-us.aspx">contact us</a>.
                            You can also learn more about your privacy rights by talking
                            to the{" "}
                            <a
                                href="https://www.privacy.org.nz/"
                                target="_blank"
                                rel="noopener">
                                Office of the Privacy Commissioner
                            </a>
                            .
                        </p>

                        <p className="text-bold">1. What does this policy do?</p>

                        <p className="text-bold">
                            1.1 This policy covers our &quot;Services&quot;, meaning
                            services or products by LifeDirect.
                        </p>

                        <p className="text-bold">
                            1.2 We are committed to your privacy and will comply with
                            applicable privacy laws,
                        </p>

                        <p>
                            including the{" "}
                            <a
                                href="http://www.legislation.govt.nz/act/public/1993/0028/232.0/DLM296645.html"
                                target="_blank"
                                rel="noopener">
                                Privacy Act 1993
                            </a>
                            , any applicable Privacy Codes and other laws when dealing
                            with your information.
                        </p>

                        <p>
                            We will also comply with the European Union&apos;s General
                            Data Protection Regulation to the extent that this applies to
                            information we collect under a particular Service (e.g., if we
                            target a particular Service at individuals living in the
                            European Union). If you are an individual based in the
                            European Union, you can{" "}
                            <a href="mailto:team@lifedirect.conz">email us</a> to clarify
                            how we treat your information.
                        </p>

                        <p className="text-bold">2. What information do we collect?</p>

                        <p>
                            We collect information so that we can provide you with our
                            Services, and so that we can build new Services. We may
                            collect the following information, some of which is personal
                            information:
                        </p>

                        <p className="text-bold">2.1 Information you give us, such as:</p>

                        <p>
                            <span className="text-bold">Account information</span>
                            <span>
                                . When you sign up for a Service, we may require your
                                name, username, password, email address, date of birth,
                                demographic information or other personal information.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Application forms</span>
                            <span>
                                . We collect information when you complete an application
                                for any of our Services or for services that we connect
                                to. Some information on application forms is particularly
                                sensitive, for example health information provided as part
                                of an application for life insurance.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Authentication information</span>
                            <span>
                                . To help create and maintain a trusted environment, we
                                may collect identification (like a photo of your
                                government-issued ID, such as a firearms licence) or other
                                authentication information. You will be told at the time
                                of collection when this applies.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Payment information</span>
                            <span>
                                . We may collect your financial information (like your
                                bank account or credit card information) when you make
                                payments to third parties using our Services (see below
                                for how we use and hold this).
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Communications and phone calls
                            </span>
                            <span>
                                . When you communicate with us in relation to a Service,
                                we may collect information about your communication and
                                any other information you provide. This includes when you
                                call us, in which case we may retain your call for fraud
                                prevention, internal training and quality assurance
                                purposes.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Other information</span>
                            <span>
                                . You may otherwise provide us information directly (by
                                email or in other formats), such as when you provide
                                feedback, fill in a form, conduct a search, update or add
                                information to our Services, respond to surveys or other
                                research activities, participate in promotions or use
                                other features of our Services.
                            </span>
                        </p>

                        <p className="text-bold">
                            2.2 Information we automatically collect, such as:
                        </p>

                        <p>
                            <span className="text-bold">Usage information</span>
                            <span>
                                . We collect information about your interactions with our
                                Services and emails and messages generated through your
                                use of the Services.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Location information</span>
                            <span>
                                . When you use certain features of our Services, we may
                                collect different types of information about your general
                                location (e.g. your physical or postal address or your
                                device&apos;s IP address).
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Analytics</span>
                            <span>
                                . We automatically collect analytics information when you
                                use our Services, even if you have not created an account
                                or logged in. We may use third party providers (such as
                                Google Analytics) to help us with this. Information
                                collected includes, among other things: details about how
                                you&apos;ve used our Services (including links to third
                                party applications), your IP address, access times,
                                hardware and software information, device information,
                                device event information (e.g. crashes, browser type), and
                                the pages, applications or services you&apos;ve viewed or
                                engaged with before or after using our Services.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Via cookies and similar technologies
                            </span>
                            <span>
                                . We use cookies, pixels, beacons and other similar
                                technologies to collect and store information about you
                                when you use our Services (e.g. through Google Analytics
                                or Google Tag Manager) and this collection may occur
                                across devices. Cookies may be &quot;session&quot; cookies
                                (which temporarily store information and expire when you
                                close your browser) or &quot;persistent&quot; cookies
                                (e.g. when you select &quot;remember me&quot; when logging
                                in). We may also allow our business partners to use these
                                technologies on our Services (e.g. for advertising and
                                remarketing), or engage others to track your behaviour on
                                our behalf. You may disable the usage of cookies through
                                your browser settings. Some Services also use social media
                                features (e.g. the Facebook Like button or widgets, such
                                as a share button or mini programmes running onsite) which
                                may collect your IP address and the pages you visit, and
                                which are generally governed by the terms of the social
                                media business providing the features.
                            </span>
                        </p>

                        <p className="text-bold">
                            2.3 Information from third parties or external sources, such
                            as:
                        </p>

                        <p>
                            <span className="text-bold">
                                Information from third party services you use
                            </span>
                            <span>
                                . If you link, connect, or log in to your account with a
                                third party service (e.g. Facebook or Trade Me), the third
                                party service may send us information such as your
                                registration and profile information from that service.
                                This information varies and is controlled by that service
                                or as authorised by you via your privacy settings at that
                                service.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Analytics information</span>
                            <span>
                                . We may collect analytics information from third parties
                                (e.g. we may use Google Analytics, Google Analytics
                                Demographics and Interest, Google AdWords, Flurry, and
                                Nielsen//Online counting system). Analytics information is
                                generally anonymised, but may include the device
                                you&apos;re using, the applications you use, your
                                operating system, your IP address, and your
                                geo-demographic information.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Information from public sources
                            </span>
                            <span>
                                . To the extent permitted by applicable law, we may
                                receive additional information about you that is publicly
                                available.
                            </span>
                        </p>

                        <p className="text-bold">3. How do we use your information?</p>

                        <p>
                            We use your personal information to provide you with Services.
                            We also use your personal information with information from
                            others so that we can analyse patterns across our data and
                            improve our Services. For example, we can look across our
                            Services to make sure we invest in product improvements that
                            deliver benefits.{" "}
                        </p>

                        <p>
                            We understand that personal information in your insurance
                            application forms or which you disclose on phone calls (such
                            as health information about you or your dependents or
                            beneficiaries) may be particularly sensitive. We will only use
                            or disclose this information for purposes related to the
                            relevant Service (e.g. providing this information securely to
                            your chosen insurers or their third-party insurance software
                            providers, or contacting you about annual insurance reviews).
                            We won&apos;t use this information for purposes unrelated to
                            LifeDirect, or your insurance needs, without your permission
                            unless this is required for fraud prevention, law enforcement
                            purposes or by law. However, we may still use and share this
                            information where it is in anonymised form (e.g., aggregated
                            information that no longer identifies or references an
                            individual User), as described in clause 4.8.
                        </p>

                        <p>We may use your personal information in the following ways:</p>

                        <p className="text-bold">
                            3.1 To contact you about your account or use.
                        </p>

                        <p>
                            We may contact you by phone, email or text message in relation
                            to your account or use. You can unsubscribe from emailed
                            newsletters and advertising materials.
                        </p>

                        <p className="text-bold">
                            3.2 To help provide our Services, for example:
                        </p>

                        <p>
                            <span className="text-bold">
                                To enable your use of our Services
                            </span>
                            <span>
                                . We may enable you to make payments on our Services,
                                verify your address (and we may use third parties to help
                                enable your use of our Services – see clause 4.3 below).
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                To personalise or enrich our Services
                            </span>
                            <span>
                                . We may personalise, improve or enrich your experience
                                by, among other things:
                            </span>
                        </p>
                        <div>
                            <ul>
                                <li>
                                    assisting with annual insurance reviews and keeping
                                    your client record;
                                </li>
                                <li>
                                    not asking you to log in unnecessarily (i.e. we may
                                    save your credentials for you);
                                </li>
                                <li>ranking search results</li>
                            </ul>
                        </div>
                        <p>
                            <span className="text-bold">To manage our Services</span>
                            <span>
                                . We may provide, operate, protect, improve, optimise and
                                otherwise manage our Services, including by using
                                analytics, accounting and customer relationship management
                                tools.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">To conduct research</span>
                            <span>
                                . We may conduct internal research in relation to
                                Users&apos; demographics, interests, and behaviour to
                                better understand and serve our community.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">For training purposes</span>
                            <span>
                                . We may use your personal information to help train and
                                improve our staff who help provide or support the Services
                                (e.g. customer support staff may have access to calls for
                                training purposes).
                            </span>
                        </p>

                        <p className="text-bold">
                            3.3 For trust, safety and assurance, for example to:
                        </p>

                        <p>
                            <span className="text-bold">
                                Prevent harm to us or others
                            </span>
                            <span>
                                . We may use your information to detect and prevent fraud,
                                security incidents, and other harmful activity (e.g. if we
                                believe you have used another person&apos;s payment card
                                or payment details without their permission or knowledge,
                                we may disclose your personal information relating to the
                                transaction to that person or their bank).
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Ensure we comply with our obligations
                            </span>
                            <span>
                                . We will use information to ensure we comply with the
                                law, including disclosures authorised under the{" "}
                                <a
                                    href="http://www.legislation.govt.nz/act/public/1993/0028/232.0/DLM296645.html"
                                    target="_blank"
                                    rel="noopener">
                                    Privacy Act 1993
                                </a>
                                .
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Allow our business to be audited
                            </span>
                            <span>
                                . We may use your information to allow for auditing of our
                                Services and businesses.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Verify the identity of users or third parties
                            </span>
                            <span>
                                . We may verify your identity or authenticate information
                                provided by you, including information relating to others.
                            </span>
                        </p>

                        <p className="text-bold">
                            3.4 For advertising and marketing, for example to:
                        </p>

                        <p>
                            <span className="text-bold">
                                Send you promotional messages, marketing or advertising
                                about our Services, including customised promotions,
                                marketing or advertising
                            </span>
                            <span>
                                . We may send or show you promotional messages, marketing,
                                advertising, and other information about our Services
                                including information that we think may be of interest to
                                you. You authorise us to use your listing content for such
                                purposes, including through display advertising,
                                electronic direct mail or on social media.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Send you customised third-party advertising (unless you
                                opt out)
                            </span>
                            <span>
                                . We may send or show you third-party promotional
                                messages, marketing, advertising (including on-site
                                display advertising), and other information that may be of
                                interest to you based on your communication preferences
                                (including information about and campaigns of our business
                                partners).
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">Run promotions</span>
                            <span>
                                . We may use your information to administer referral
                                programmes, rewards, surveys, sweepstakes, contests, or
                                other promotional activities or events sponsored or
                                managed by us or our business partners.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Measure and improve our advertising
                            </span>
                            <span>
                                . We may use your information to measure the effectiveness
                                of our advertising and make improvements.
                            </span>
                        </p>

                        <p className="text-bold">
                            3.5 For other things, with your express consent.
                        </p>

                        <p>
                            We may disclose your information at your direction, or
                            otherwise as you agree with us. Please note that we:
                        </p>

                        <p>Do not have access to your passwords.</p>

                        <p>
                            Only use your credit card details for purposes related to your
                            transactions and for fraud prevention or law enforcement
                            purposes.
                        </p>

                        <p className="text-bold">
                            4. Who do we disclose your information to?
                        </p>

                        <p>
                            We may need to disclose your personal information to others to
                            provide you with our Services, to improve our Services or
                            uphold the law or our terms. We may also disclose your
                            personal information in the following ways:
                        </p>

                        <p className="text-bold">
                            4.1 To others with your consent, for example when you:
                        </p>

                        <p>
                            <span className="text-bold">Ask us or authorise us</span>
                            <span>
                                . We&apos;ll share your information when you authorise us
                                to send this to a third party.
                            </span>
                        </p>

                        <p className="text-bold">
                            4.2 To our service providers, to provide and manage our
                            Services.
                        </p>

                        <p>
                            In order to provide and manage our Services, we may need to
                            disclose some of your personal information to our third party
                            service providers. These providers have limited access to your
                            personal information to perform tasks on our behalf, and are
                            contractually obliged to use your personal information
                            consistently with this Privacy Policy. These disclosures will
                            be consistent with the Service-specific terms and the rest of
                            this privacy policy.
                        </p>

                        <p className="text-bold">4.3 To other third parties, such as:</p>

                        <p>
                            <span className="text-bold">Insurers and advisers</span>
                            <span>
                                . We may give your insurance application form and other
                                personal information to your chosen insurer so that they
                                can facilitate your insurance.
                            </span>
                        </p>

                        <p className="text-bold">
                            4.4 To authorities or other entities to uphold the law or our
                            terms, for example:
                        </p>

                        <p>
                            <span className="text-bold">
                                For legal compliance or law enforcement purposes
                            </span>
                            <span>
                                . We may disclose your personal information to courts, law
                                enforcement or government agencies, or third parties, to
                                the extent we believe that disclosure is appropriate or
                                permitted by the Privacy Act.
                            </span>
                        </p>

                        <p className="text-bold">4.5 To our other Services.</p>

                        <p>
                            We may disclose your personal information to entities partly
                            or fully owned by LifeDirect which are governed by this
                            privacy policy, subject to your privacy options and the
                            service-specific terms in clause 8
                        </p>

                        <p className="text-bold">
                            4.6 To others where necessary to reorganise or sell our
                            business.
                        </p>

                        <p>
                            If we undertake or are involved in any merger, acquisition,
                            reorganisation, sale of assets, bankruptcy, or insolvency
                            event, then we may sell, transfer or share some or all of our
                            assets. In this event, we will notify you before your personal
                            information is transferred and becomes subject to a different
                            privacy policy.
                        </p>

                        <p className="text-bold">
                            4.7 To other third parties, where your identity is anonymised.
                        </p>

                        <p>
                            We may share non-personally identifiable information (e.g.,
                            aggregated information about our Users that we combine
                            together so that it no longer identifies or references an
                            individual User), for industry and market analysis,
                            demographic profiling, marketing and advertising, and other
                            business purposes.
                        </p>

                        <p className="text-bold">
                            4.8 Also, some interactions with our sites and Services may be
                            subject to additional third party terms. For example
                        </p>

                        <p>
                            <span className="text-bold">External links</span>
                            <span>
                                . Our Services may contain links to third party websites
                                or services. We don&apos;t own or control those websites
                                or services or endorse them in any way and, if you
                                interact with them, you may be providing information
                                directly to the third party concerned. The third parties
                                will have their own rules about the collection, use, and
                                disclosure of your personal information. We encourage you
                                to review their privacy policies accordingly.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Certain embedded third party services
                            </span>
                            <span>
                                . Some of our Services use embedded third party services,
                                e.g. Google Maps/Earth. Use of those services may be
                                subject to third party terms, e.g. Google Maps/Earth
                                Additional Terms of Use and the Google Privacy Policy.
                            </span>
                        </p>

                        <p>
                            <span className="text-bold">
                                Information indexed by search engines
                            </span>
                            <span>
                                . Information you share publicly on our Services may be
                                indexed through third party search engines. We do not
                                control the practices of third party search engines, and
                                they may use caches containing your outdated information.
                            </span>
                        </p>

                        <p className="text-bold">
                            5. How do we hold and protect your information?
                        </p>

                        <p>
                            We work hard to ensure we keep your personal information safe
                            and to avoid any harm to you or your personal information. We
                            would notify you and the Office of the Privacy Commissioner if
                            a potentially harmful data breach of your personal information
                            did occur. We delete personal information after it is no
                            longer reasonably necessary for us to use it. In particular:
                        </p>

                        <p className="text-bold">
                            5.1 We use industry recognised security practices and
                            measures.
                        </p>

                        <p>
                            We are committed to keeping your personal information as safe
                            and secure as possible and follow generally accepted industry
                            standards to protect the personal information submitted to us,
                            both during transmission and once we receive it.
                        </p>

                        <p>
                            We continuously implement and update our administrative,
                            technical, and physical security measures to help protect your
                            personal information against unauthorised access, loss,
                            destruction, or alteration. However, despite all of our
                            measures we can&apos;t 100% guarantee the security of the
                            transmission or storage of your personal information.
                        </p>

                        <p className="text-bold">
                            5.2 We store your information in New Zealand or overseas
                            subject to standards that will comply with our privacy
                            obligations
                        </p>

                        <p>
                            You authorise us to transfer, store, and process your personal
                            information in New Zealand and possibly other countries. Where
                            we store information overseas, or a third party we use to
                            provide our Services stores information overseas (e.g. Amazon
                            Web Services, Microsoft Azure etc), we will ensure that the
                            storage complies with New Zealand privacy obligations.
                        </p>

                        <p className="text-bold">
                            5.3 We retain your information as long as reasonably required,
                            unless we are legally required to hold it longer.
                        </p>

                        <p>
                            We will keep your personal information for as long as is
                            reasonably required for us to use it in accordance with this
                            policy, unless we are required by law to hold it longer. In
                            particular, we may retain:
                        </p>

                        <p>
                            Recordings of your phone calls, where necessary for business
                            or compliance purposes, to help prevent fraud or facilitate
                            legal processes).
                        </p>

                        <p>
                            Other personal information that is deleted by you may persist
                            for a limited time in our backup systems.
                        </p>

                        <p className="text-bold">
                            5.4 Credit card Information is held subject to stringent
                            standards.
                        </p>

                        <p>
                            Credit and debit card information is sensitive and we protect
                            it to stringent security standards. We use a number of
                            procedures and different technologies to help protect your
                            credit and debit card information from unauthorised access,
                            use and disclosure.
                        </p>

                        <p className="text-bold">6. How can I manage my information?</p>

                        <p className="text-bold">
                            We know your circumstances, details and preferences will
                            change. So, we&apos;ve built a number of tools for you to
                            manage your personal information. You can:
                        </p>

                        <p className="text-bold">6.1 Update your information by:</p>

                        <p>
                            <span className="text-bold">
                                Updating your account information
                            </span>
                            <span>
                                . In most cases, you may review and update the account
                                information we hold about you (such as your email address,
                                password and phone numbers) by logging into MyLifeDirect
                                and updating the information in your account. If this
                                isn&apos;t available and you want to update your
                                information, please get in touch with us.
                            </span>
                        </p>

                        <p className="text-bold">
                            6.2 Ask us to update, provide or delete your information
                        </p>

                        <p>
                            If you wish to access or correct any of your personal
                            information, you can{" "}
                            <a href="/our-service/contact-us.aspx">contact us</a>{" "}
                            directly. We will respond to your request promptly, and always
                            within 5 working days. If you want us to delete any personal
                            information you have submitted to us, please contact us. We
                            will consider each request for deletion on a case by case
                            basis taking into account the terms and conditions of the
                            particular Service(s) and our legal obligations to retain
                            certain information. Note that information that you have
                            shared with others may continue to be publicly visible on our
                            Services in association with your first name, even after your
                            account is cancelled.
                        </p>

                        <p>
                            If you want to cancel your account for any of our Services,
                            please <a href="/our-service/contact-us.aspx">contact us</a>.
                        </p>

                        <p className="text-bold">7. How do we amend this policy?</p>

                        <p className="text-bold">
                            We may make updates from time to time by a LifeDirect site
                            announcement or other notice, usually with two weeks&apos;
                            notice or more.
                        </p>

                        <p>
                            Occasionally urgent changes will be required, but we will
                            generally give you at least two weeks&apos; notice by
                            LifeDirect site announcement to review any changes, unless the
                            Service-specific terms do not require notice of changes to
                            them. For significant changes we may notify you directly via
                            email. As we update this policy from time to time, we
                            encourage you to periodically review this page for the latest
                            information on our privacy practices.
                        </p>

                        <p className="text-bold">
                            8. What do the terms in this policy mean?
                        </p>

                        <p className="text-bold">8.1 &quot;User&quot;</p>

                        <p>User means anyone using our Services. </p>

                        <p className="text-bold">8.2 &quot;personal information&quot;</p>

                        <p>
                            Personal information means information about an identifiable
                            individual, as defined by the{" "}
                            <a
                                href="http://www.legislation.govt.nz/act/public/1993/0028/232.0/DLM296645.html"
                                target="_blank"
                                rel="noopener">
                                Privacy Act 1993
                            </a>
                            .
                        </p>

                        <p className="text-bold">8.3 &quot;Services&quot;</p>

                        <p>Services means our Services, as defined in clause 1.1.</p>

                        <p className="text-bold">
                            8.4 &quot;we&quot;, &quot;us&quot; or &quot;our&quot;
                        </p>

                        <p>We, us, or our means: LifeDirect 2020 Limited</p>

                        <p className="text-bold">
                            8.5 &quot;you&quot; or &quot;your&quot;
                        </p>

                        <p>
                            You or your, in relation to any personal information, means
                            any individual who is identifiable from the personal
                            information.
                        </p>
                    </React.Fragment>
                )}
            </LModal>
        </React.Fragment>
    );
};

export default SecurityAndPrivacyModal;
